/**
 * Schema Zod do payload de Contrato Recorrente.
 * Espelha `ContratoRecorrentePayload` em types/payloads.ts.
 */

import { z } from 'zod';

import { quantidadeSchema, textoLongoSchema } from './common.js';

const pacoteGestaoTISchema = z.enum(['essencial', 'professional', 'premium']);

const perfilAmbienteSchema = z.enum(['simples', 'moderado', 'critico']);

const tipoOnboardingSchema = z.enum(['none', 'express', 'standard', 'enterprise']);

// Novo modelo dimensional (2026-05+)
const onboardingBreakdownSnapshotSchema = z.object({
  baseFixo: z.number().nonnegative(),
  linhas: z.array(
    z.object({
      label: z.string().min(1),
      qtd: z.number().nonnegative(),
      unitario: z.number().nonnegative(),
      subtotal: z.number().nonnegative(),
    }),
  ),
  subtotalAntesMultiplicador: z.number().nonnegative(),
  perfil: perfilAmbienteSchema,
  multiplicador: z.number().min(1).max(3),
  total: z.number().nonnegative(),
});

const onboardingPropostaSchema = z.discriminatedUnion('modo', [
  z.object({ modo: z.literal('nenhum') }),
  z.object({
    modo: z.literal('sugerido'),
    valorFinal: z.number().nonnegative(),
    valorSugerido: z.number().nonnegative(),
    breakdown: onboardingBreakdownSnapshotSchema,
  }),
  z.object({
    modo: z.literal('manual'),
    valorFinal: z.number().nonnegative(),
    valorSugerido: z.number().nonnegative(),
    breakdown: onboardingBreakdownSnapshotSchema,
  }),
]);

/** União: aceita string legado (propostas antigas) OU objeto novo */
const onboardingPropostaOuLegadoSchema = z.union([tipoOnboardingSchema, onboardingPropostaSchema]);

const recursosAmbienteSchema = z.object({
  dispositivos: z.number().int().min(0),
  usuarios: z.number().int().min(0),
  servidores: z.number().int().min(0),
  horasSuporte: z.number().nonnegative(),
});

const ambienteClienteSchema = z.object({
  perfil: perfilAmbienteSchema,
  multiplicador: z.number().min(0).max(3),
});

// ── Pacotes Adicionais (discriminated union por tipo) ─────────────

const backupItemSchema = z.object({
  tipo: z.enum([
    'documents',
    'm365',
    'gb-adicional',
    'estacao',
    'servidor-fisico',
    'servidor-virtual',
  ]),
  quantidade: quantidadeSchema,
});

const emailItemSchema = z.object({
  tipo: z.enum([
    'imap-5',
    'imap-25',
    'imap-50',
    'imap-100',
    'imap-200',
    'exchange-50',
    'exchange-100',
    'exchange-200',
  ]),
  quantidade: quantidadeSchema,
});

const wirelessItemSchema = z.object({
  tipo: z.enum([
    'ap-6ghz',
    'ap-longo-alcance',
    'ap-externo',
    'sw-8p',
    'sw-8p-poe',
    'sw-24p',
    'sw-24p-poe',
    'sw-48p',
    'sw-48p-poe',
  ]),
  quantidade: quantidadeSchema,
});

const firewallConfigSchema = z.object({
  marca: z.enum(['mikrotik', 'unifi', 'fortinet']),
  modelo: z.string().min(1).max(100),
  licencas: z.array(z.string().min(1)).optional(),
});

export const pacoteAdicionalSchema = z.discriminatedUnion('tipo', [
  z.object({ tipo: z.literal('backup-nuvem'), itens: z.array(backupItemSchema).min(1) }),
  z.object({ tipo: z.literal('email-profissional'), itens: z.array(emailItemSchema).min(1) }),
  z.object({ tipo: z.literal('rede-wireless'), itens: z.array(wirelessItemSchema).min(1) }),
  z.object({ tipo: z.literal('firewall-gerenciado'), firewall: firewallConfigSchema }),
  z.object({ tipo: z.literal('opcional-m365'), usuarios: quantidadeSchema }),
  z.object({
    tipo: z.literal('opcional-antivirus'),
    usuarios: quantidadeSchema,
    nivel: z.enum(['av', 'edr']),
  }),
]);

// ── Schema principal do payload ────────────────────────────────────

// HaaS - Hardware as a Service (Adendo HaaS v.1.1)
const haasItemTipoSchema = z.enum([
  'mikrotik',
  'mikrotikWireless',
  'accessPoint',
  'switch',
  'nobreak',
]);

const propostaHaasItemSchema = z.object({
  tipo: haasItemTipoSchema,
  modelo: z.string().min(1).max(200),
  quantidade: z.number().int().min(1).max(9999),
  valorMensalUnit: z.number().nonnegative().finite(),
  precoVendaUnit: z.number().nonnegative().finite(),
  pctHardware: z.number().int().min(0).max(100),
});

// Snapshot por proposta de cada Modulo Premium contratado.
const propostaPremiumModuloSchema = z.object({
  id: z.string().min(1).max(40),
  nome: z.string().min(1).max(120),
  escopoResumido: z.string().min(1).max(500),
  valorHora: z.number().nonnegative().finite(),
  horas: z.number().int().nonnegative(),
  valorMensal: z.number().nonnegative().finite(),
});

// ── Desconto escalonado ────────────────────────────────────────────────

export const descontoFaixaSchema = z
  .object({
    tipo: z.enum(['pct', 'valor']),
    valor: z.number().positive(),
    duracaoMeses: z.number().int().min(1).max(36),
    motivo: z.string().trim().max(200).optional(),
  })
  .refine((f) => f.tipo !== 'pct' || f.valor <= 100, {
    message: 'Desconto percentual deve ser entre 0.01 e 100.',
    path: ['valor'],
  });

export const descontoEscalonadoSchema = z.object({
  faixas: z.array(descontoFaixaSchema).min(1).max(4),
  aplicarEm: z.enum(['mensal', 'mensal-onboarding']),
});

export const contratoRecorrentePayloadSchema = z.object({
  pacotePrincipal: pacoteGestaoTISchema,
  /**
   * Proposta AVULSA (à la carte): vende só add-ons, sem pacote de gestão base.
   * Quando true, o pricing ignora pacote/inventário/premium e cobra só os add-ons;
   * as views escondem comparativo de planos e a seção de pacote. `pacotePrincipal`
   * fica como placeholder (não cobra).
   */
  semPacoteBase: z.boolean().optional(),
  pacotesAdicionais: z.array(pacoteAdicionalSchema),
  ambiente: ambienteClienteSchema,
  recursos: recursosAmbienteSchema,
  onboarding: onboardingPropostaOuLegadoSchema,
  prazoFidelidade: z.enum(['12 meses', '24 meses', '36 meses', 'Sem fidelidade']),
  diagnostico: z
    .object({
      sintoma: textoLongoSchema.optional(),
      causaRaiz: textoLongoSchema.optional(),
      impactoNegocio: textoLongoSchema.optional(),
      resultado90: textoLongoSchema.optional(),
    })
    .optional(),
  /** HaaS - opcional (vazio = sem HaaS, nao dispara Adendo) */
  haasItens: z.array(propostaHaasItemSchema).max(50).optional(),
  /** Modulos Premium - exclusivo do pacote Premium (Contrato v2026.08). */
  premiumModulos: z.array(propostaPremiumModuloSchema).max(20).optional(),
  /** Desconto promocional escalonado (ate 4 faixas temporais) */
  descontoEscalonado: descontoEscalonadoSchema.optional(),
  /**
   * Congelar reajuste IPCA por N meses (#255). Marcador comercial — não afeta pricing.
   * Pode coexistir com descontoEscalonado.
   */
  congelarReajusteMeses: z.number().int().min(1).max(36).optional(),
});
